import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { Overline } from "./Mark";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BILLS = ["Marketing bill", "Consultation bill"];
const EMPTY = { amount: "", name: "", email: "", reference: "" };

const loadCheckout = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const errMsg = (e, fallback) => e?.response?.data?.detail && typeof e.response.data.detail === "string" ? e.response.data.detail : fallback;

const Label = ({ children }) => <span className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-slate-400">{children}</span>;

export const Payment = () => {
  const [bill, setBill] = useState(BILLS[0]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const verify = async (res) => {
    try {
      const { data } = await axios.post(`${API}/verify-payment`, res);
      setPaid({ id: data?.payment_id, bill, amount: form.amount });
      setForm(EMPTY);
      toast.success("Payment successful. Thank you!");
    } catch (e) {
      toast.error(errMsg(e, "We couldn't verify this payment. Please contact us."));
    } finally {
      setBusy(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const [ok, cfg] = await Promise.all([loadCheckout(), axios.get(`${API}/payments/config`)]);
      if (!ok) throw new Error("checkout");
      const { data } = await axios.post(`${API}/create-order`, { bill_type: bill, amount: Number(form.amount), name: form.name, email: form.email, reference: form.reference || null });
      const rzp = new window.Razorpay({
        key: cfg.data?.key_id,
        order_id: data?.order_id,
        amount: data?.amount,
        currency: data?.currency,
        name: "VK Enterprises",
        description: bill,
        prefill: { name: form.name, email: form.email },
        theme: { color: "#D4AF37" },
        handler: verify,
        modal: { ondismiss: () => { setBusy(false); toast("Payment cancelled."); } },
      });
      rzp.on("payment.failed", (r) => { setBusy(false); toast.error(r?.error?.description || "Payment failed. Please try again."); });
      rzp.open();
    } catch (err) {
      setBusy(false);
      toast.error(err?.message === "checkout" ? "Couldn't load the payment window. Check your connection." : errMsg(err, "Couldn't start the payment. Please try again."));
    }
  };

  return (
    <section id="pay" className="bg-[#070E17] py-24 lg:py-36" data-testid="payment-section">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <Reveal className="min-w-0 lg:col-span-5">
          <Overline>Pay a Bill</Overline>
          <h2 className="font-serif-d mt-6 text-5xl font-medium leading-[1] text-white sm:text-6xl">Settle your bill <em className="gold-text italic">securely.</em></h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-slate-300">Choose your bill type, enter the amount you'd like to pay in US dollars and complete the payment in Razorpay's secure checkout.</p>
          <p className="mt-6 flex items-center gap-3 text-sm text-[#E6C280]"><ShieldCheck className="h-5 w-5" /> Payments processed by Razorpay</p>
        </Reveal>
        <Reveal delay={0.15} className="min-w-0 lg:col-span-6 lg:col-start-7">
          {paid ? (
            <div className="rounded-[32px] border border-[rgba(212,175,55,0.3)] bg-[#0F1E36]/70 p-8 sm:p-10" data-testid="payment-success">
              <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" />
              <h3 className="font-serif-d mt-6 text-4xl text-white">Payment received</h3>
              <p className="mt-3 text-slate-300">{paid.bill} · ${Number(paid.amount).toFixed(2)} USD</p>
              <p className="font-mono-d mt-2 break-all text-xs text-slate-400">Payment ID: {paid.id}</p>
              <button onClick={() => setPaid(null)} className="mt-8 rounded-full border border-[rgba(212,175,55,0.35)] px-6 py-3 text-sm text-white hover:border-[#D4AF37]" data-testid="payment-again-btn">Make another payment</button>
            </div>
          ) : (
            <form onSubmit={pay} className="rounded-[32px] border border-[rgba(212,175,55,0.2)] bg-[#0F1E36]/60 p-6 sm:p-10" data-testid="payment-form">
              <Label>Bill type *</Label>
              <div className="mt-3 grid grid-cols-2 gap-3" role="radiogroup">
                {BILLS.map((b) => (
                  <button type="button" key={b} onClick={() => setBill(b)} aria-pressed={bill === b} className={`rounded-2xl border px-4 py-4 text-left text-sm transition-[background-color,border-color,color] duration-300 ${bill === b ? "border-[#D4AF37] bg-[#D4AF37] text-[#070E17]" : "border-[rgba(212,175,55,0.25)] text-white hover:border-[#D4AF37]"}`} data-testid={`bill-type-${b.split(" ")[0].toLowerCase()}`}>{b}</button>
                ))}
              </div>
              <label className="mt-8 block"><Label>Amount (USD) *</Label>
                <div className="flex items-center gap-2 border-b border-[rgba(212,175,55,0.25)] focus-within:border-[#D4AF37]">
                  <span className="font-serif-d text-4xl text-[#E6C280]">$</span>
                  <input required type="number" min="1" step="0.01" inputMode="decimal" value={form.amount} onChange={set("amount")} className="font-serif-d w-full bg-transparent py-3 text-4xl text-white outline-none placeholder:text-slate-500" placeholder="0.00" data-testid="payment-amount-input" />
                </div>
              </label>
              <div className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <label className="block"><Label>Full name *</Label><input required minLength={2} value={form.name} onChange={set("name")} className="field" placeholder="Your name" data-testid="payment-name-input" /></label>
                <label className="block"><Label>Email *</Label><input required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@email.com" data-testid="payment-email-input" /></label>
                <label className="block sm:col-span-2"><Label>Invoice / reference</Label><input value={form.reference} onChange={set("reference")} className="field" placeholder="Optional" data-testid="payment-reference-input" /></label>
              </div>
              <button disabled={busy} className="group mt-10 flex w-full items-center justify-between rounded-full bg-[#D4AF37] py-3 pl-8 pr-3 text-base font-medium text-[#070E17] transition-[background-color,transform] duration-300 hover:bg-[#E6C280] active:scale-[0.98] disabled:opacity-70" data-testid="payment-submit-btn">
                {busy ? "Processing…" : `Pay${form.amount ? ` $${Number(form.amount).toFixed(2)}` : ""}`}
                <span className="grid h-11 w-11 place-items-center rounded-full bg-[#070E17] text-[#D4AF37] transition-transform duration-500 group-hover:rotate-45">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                </span>
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
